/**
 * Admin Tickets tab — list + thread view + status/priority controls.
 * Uses api(method, path, body) wrapper.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Ticket, Loader2, RefreshCw, X, Send, Clock, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';

const fmtDate = (s) => s ? new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
const STATUSES = ['open', 'in_review', 'assigned', 'resolved', 'closed'];

export default function TicketsAdminTab({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('GET', `/admin/tickets${filter ? `?status=${filter}` : ''}`);
      setRows(r.data || []);
    } catch (e) { /* */ } finally { setLoading(false); }
  }, [api, filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-tickets-tab">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">Support Tickets</h2>
          <p className="text-sm text-slate-500 mt-0.5">Customer raised tickets · respond &amp; manage status</p>
        </div>
        <Button variant="outline" size="sm" onClick={load}><RefreshCw size={14} className="mr-1.5" /> Refresh</Button>
      </div>

      <div className="flex gap-2 mb-3 flex-wrap">
        {['', ...STATUSES].map(s => (
          <button key={s || 'all'} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-full ${filter === s ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid={`tickets-filter-${s || 'all'}`}
          >{s ? s.replace('_', ' ') : 'all'}</button>
        ))}
      </div>

      {loading && <Loader2 size={20} className="animate-spin text-slate-400" />}
      {!loading && rows.length === 0 && (
        <div className="border-2 border-dashed border-slate-200 rounded-2xl p-10 text-center bg-white">
          <Ticket size={36} className="mx-auto text-slate-300 mb-3" />
          <div className="font-semibold text-slate-700">No tickets in this filter</div>
        </div>
      )}

      <div className="space-y-2">
        {rows.map(t => (
          <button key={t.id} onClick={() => setSelected(t)} className="w-full text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-emerald-300 transition-colors" data-testid={`ticket-row-${t.ticket_number}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-mono text-xs text-slate-400">{t.ticket_number}</span>
                  <Status status={t.status} />
                  <Priority p={t.priority} />
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 uppercase font-semibold">{t.category}</span>
                </div>
                <div className="font-semibold text-slate-900">{t.subject}</div>
                <div className="text-xs text-slate-500 mt-1">From {t.customer_name} · {t.customer_phone}</div>
              </div>
              <ChevronRight size={16} className="text-slate-400" />
            </div>
            <div className="text-[10px] text-slate-400 mt-2 flex items-center gap-1"><Clock size={10} /> {fmtDate(t.updated_at || t.created_at)} · {t.messages?.length || 0} message{t.messages?.length !== 1 ? 's' : ''}</div>
          </button>
        ))}
      </div>

      <Drawer open={!!selected} onClose={() => { setSelected(null); load(); }} ticket={selected} api={api} />
    </div>
  );
}

function Status({ status }) {
  const map = {
    open: 'bg-amber-100 text-amber-700', in_review: 'bg-blue-100 text-blue-700',
    assigned: 'bg-indigo-100 text-indigo-700', resolved: 'bg-emerald-100 text-emerald-700',
    closed: 'bg-slate-100 text-slate-600',
  };
  return <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase ${map[status]}`}>{(status || 'open').replace('_', ' ')}</span>;
}
function Priority({ p }) {
  const c = { low: 'bg-slate-400', normal: 'bg-blue-500', high: 'bg-amber-500', urgent: 'bg-red-500' }[p] || 'bg-slate-400';
  return <span className="inline-flex items-center gap-1 text-[10px] text-slate-500"><span className={`w-2 h-2 rounded-full ${c}`} />{p}</span>;
}

function Drawer({ open, onClose, ticket, api }) {
  const [detail, setDetail] = useState(ticket);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDetail(ticket); }, [ticket]);

  const refresh = useCallback(async () => {
    if (!ticket?.id) return;
    const r = await api('GET', `/admin/tickets/${ticket.id}`);
    setDetail(r.data);
  }, [ticket, api]);

  const reply = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api('POST', `/admin/tickets/${ticket.id}/reply`, { body });
      setBody('');
      await refresh();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    try { await api('PATCH', `/admin/tickets/${ticket.id}`, { status }); await refresh(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  if (!open || !detail) return null;
  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-black/40" />
      <div className="w-full max-w-2xl bg-white shadow-2xl overflow-y-auto flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="admin-ticket-drawer">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <div>
            <div className="font-mono text-xs text-slate-400">{detail.ticket_number}</div>
            <h3 className="font-bold text-slate-900">{detail.subject}</h3>
            <div className="text-xs text-slate-500">{detail.customer_name} · {detail.customer_phone} · {detail.customer_email}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex flex-wrap gap-2 items-center text-xs">
          <span className="font-semibold text-slate-600">Set status:</span>
          {STATUSES.map(s => (
            <button key={s} onClick={() => setStatus(s)} disabled={detail.status === s}
              className={`px-2 py-1 rounded-full font-semibold uppercase ${detail.status === s ? 'bg-emerald-600 text-white' : 'bg-white border border-slate-300 text-slate-600 hover:bg-slate-100'}`}
              data-testid={`set-status-${s}`}
            >{s.replace('_', ' ')}</button>
          ))}
        </div>

        <div className="flex-1 p-4 space-y-3 overflow-y-auto">
          {(detail.messages || []).map(m => (
            <div key={m.id} className={`flex ${m.from === 'admin' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.from === 'admin' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-900'}`}>
                <div className="text-[10px] opacity-70 mb-1 font-semibold">{m.from_name} · {fmtDate(m.created_at)}</div>
                <div className="whitespace-pre-line">{m.body}</div>
              </div>
            </div>
          ))}
        </div>

        {detail.status !== 'closed' && (
          <div className="border-t border-slate-200 p-3 bg-slate-50">
            <div className="flex gap-2">
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Reply to customer…" rows={2} className="flex-1" data-testid="admin-ticket-reply-body" />
              <Button onClick={reply} disabled={busy || !body.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="admin-ticket-reply-submit">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
